import { useBuy } from "@/context/buy";
import { useRouter } from "next/router";
import { useCallback, useEffect, useRef, useState } from "react";
import { decryptInteger, encryptInteger } from "@/Utils/encryptInteger";
import { Skeleton } from "@mui/material";
import { formatMonthDayYear } from "@/Utils/dateFormat";
import Link from "next/link";

export default function ArtistProfileShowsTab({
  artist,
  setSelectedShow,
  ref,
  setShowData,
}) {
  const router = useRouter();

  const [data, setData] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const { openSingleModal, setCustomModalData } = useBuy();

  const handleScroll = useCallback(() => {
    if (ref.current) {
      const div = ref.current;
      const scrollTop = div.scrollTop;
      const scrollHeight = div.scrollHeight - div.clientHeight;
      const scrollPercent = (scrollTop / scrollHeight) * 100;
      if (data?.length >= (currentPage + 1) * 10 && scrollPercent >= 80) {
        setCurrentPage((prevPage) => prevPage + 1);
      }
    }
  }, [currentPage, data, setCurrentPage]);

  useEffect(() => {
    const div = ref.current;
    if (div) {
      div.addEventListener("scroll", handleScroll);
      return () => div.removeEventListener("scroll", handleScroll);
    }
  }, [handleScroll]);

  useEffect(() => {
    setShowData(data);
  }, [data]);

  useEffect(() => {
    function getToken() {
      try {
        let encryptedData = localStorage.getItem("user_data");
        if (encryptedData) {
          let data = JSON.parse(decryptInteger(encryptedData));
          return data?.web_token || null;
        }
        return null;
      } catch (error) {
        console.error("Error fetching web token:", error);
        return null;
      }
    }
    const webToken = getToken();
    const headers = webToken ? { Authorization: `Bearer ${webToken}` } : {};

    async function fetchData() {
      setLoading(true);
      try {
        const formData = new FormData();
        formData.append("action", "getShowListByArtist");
        formData.append("page_index", currentPage);
        formData.append("user_id", artist?.id ?? "");

        const response = await fetch(
          `${process.env.NEXT_PUBLIC_BACKEND_URL}/webapi/manageShow`,
          {
            method: "POST",
            body: formData,
            headers,
          }
        );

        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const data = await response.json();
        if (currentPage == 0) {
          setData([...data?.data]);
        } else {
          setData((prevData) => [...prevData, ...data?.data]);
        }
      } catch (error) {
        console.error("Error fetching show list:", error);
      } finally {
        setLoading(false);
      }
    }

    if (artist?.id !== undefined) {
      fetchData();
    }
  }, [artist?.id, currentPage]);

  const slugify = (str) =>
    str
      ?.toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/(^-|-$)/g, "");
  return (
    <div className="mt-[45px] lg:mt-[5px] lg:pr-[19px] overflow-auto ">
      <div className="grid lg:my-[50px] grid-cols-2  lg:grid-cols-3 gap-x-[37px] gap-y-[48px] lg:gap-y-[137px] ">
        {data && !loading && data.length == 0 ? (
          <div className=" py-20 text-center col-span-3 ">
            <p className=" text-2xl font-[600] ">
              {artist?.name}&rsquo;s completed and upcoming shows will appear
              here
            </p>
          </div>
        ) : loading ? (
          Array.from({ length: 5 }).map((_, i) => (
            <div
              key={i}
              className="gradient-skeleton w-full min-w-[120px] min-h-[120px] lg:w-full h-full lg:h-[462px]"
            >
              <Skeleton
                variant="circle"
                width="100%"
                height="100%"
                sx={{ margin: "0 auto", borderRadius: "16px" }}
              />
            </div>
          ))
        ) : (
          data.map((show, idx) => {
            const showDateString = show?.show_date || "";
            const datePart = showDateString.split(" ")[0];
            const finalDate = formatMonthDayYear(datePart);
            let currentDate = new Date();
            let show_at_time = new Date(show?.show_at);
            let showName = false;
            if (currentDate > show_at_time) {
              showName = true;
            }
            const city = slugify(show?.venue?.city);
            const state = slugify(show?.venue?.state);
            const showNames = slugify(show?.name);
            const venueName = slugify(show?.venue?.name);
            const showDate = show?.show_date.split(" ")[0];

            const fullPath = `/shows/${city}-${state}/${show.id}-for-${showNames}-at-${venueName}-on-${showDate}`;
            return (
              <div
                key={idx}
                onClick={() => router.push(fullPath)}
                className=" lg:bg-[#1F222A] flex flex-col h-full relative cursor-pointer hover:scale-[101%] transition-transform duration-300 p-4 rounded-[40px] shadow-[0px_4px_60px_0px_#04060F0D] "
              >
                <div className=" relative w-full min-w-[120px]">
                  <img
                    src={show?.banner}
                    className="rounded-[8px] lg:rounded-[32px] min-w-[120px] min-h-[120px] aspect-square lg:aspect-auto lg:max-h-[250px] lg:min-h-[250px] object-cover w-full "
                    alt=""
                  />
                  <div className=" absolute bottom-[7px] lg:hidden left-0 flex justify-center  z-[99] w-full ">
                    {!showName && (
                      <div className="flex flex-col gap-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedShow(show);
                            openSingleModal("buy");
                          }}
                          className={`main_btn_bg px-[10px] py-[6px] rounded-[8px]`}
                        >
                          <p className="text-[10px] font-[700]">
                            Get Tickets
                          </p>
                        </button>
                        {show?.is_ticket_booked && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              router.push(`/tickets/${show?.booking_id}`);
                            }}
                            className="view_ticket_btn px-[10px] py-[6px] rounded-[8px] lg:w-3/4"
                          >
                            <p className="text-[10px] min-w-max py-1 font-[700]">View Ticket</p>
                          </button>
                        )}
                      </div>
                    )}

                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedShow(show);
                        openSingleModal("buy");
                      }}
                      className={` ${showName ? "" : "hidden"
                        } bg-[#08080A] px-[10px] py-[6px] rounded-[8px]`}
                    >
                      <p
                        onClick={(e) => {
                          e.stopPropagation();
                        }}
                        className={`${show?.is_ticket_booked ? "px-[10px] py-[6px]" : ""
                          } text-[10px] font-[700]`}
                      >
                        {show?.title}
                      </p>
                    </button>
                  </div>
                </div>
                <div className="mt-3 hidden lg:flex flex-col gap-[14px] py-[6px] ">
                  <p className="mb-0 text-2xl font-[700] leading-[28.8px] text-center ">
                    {show.name}
                  </p>
                  <p className="mb-0 text-[#C4C4C4] text-lg leading-[25.2px] text-center ">
                    {finalDate}
                  </p>
                </div>
                <div className="flex-grow"></div>
                {!showName && (
                  <div className={`mt-4 w-full hidden lg:flex flex-col gap-3 ${show?.venue?.can_buy_tickets ? "" : "disableBuyTicket"}`}>
                    {show?.is_ticket_booked ? (
                      <>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            router.push(`/tickets/${show?.booking_id}`);
                          }}
                          className="view_ticket_btn w-full rounded-2xl"
                        >
                          <p className="py-[18px] text-lg font-extrabold leading-[22px]">
                            View Ticket
                          </p>
                        </button>

                        <button
                          onClick={(e) => {
                            e.stopPropagation();

                            if (show.venue_ticket_web_link) {
                              openSingleModal("custom");
                              setCustomModalData({
                                showId: show.id,
                                link: show.venue_ticket_web_link,
                              });
                              return;
                            }

                            if (show?.venue?.can_buy_tickets) {
                              setSelectedShow(show);
                              openSingleModal("buy");
                            }
                          }}

                          className="main_btn_bg w-full rounded-2xl"
                        >
                          <p className="py-[18px] text-lg font-extrabold leading-[22px] text-center">
                            Get Tickets
                          </p>
                        </button>
                      </>
                    ) : (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          if (show.venue_ticket_web_link) {
                            openSingleModal("custom");
                            setCustomModalData({
                              showId: show.id,
                              link: show.venue_ticket_web_link,
                            });
                            return;
                          }
                          if (show?.venue?.can_buy_tickets) {
                            setSelectedShow(show);
                            openSingleModal("buy");
                          }
                        }}
                        className="main_btn_bg  w-full rounded-2xl"
                      >
                        <p className="py-[18px] text-lg font-extrabold leading-[22px]">
                          Get Tickets
                        </p>
                      </button>
                    )}
                  </div>
                )}
                <div className="hidden lg:block">
                  <button
                    onClick={() => {
                      if (show?.is_ticket_booked) {
                        router.push(`/tickets/${show?.booking_id}`);
                      }
                    }}
                    className={` ${showName ? "" : "hidden "
                      } bg-[#08080A] mt-4 w-full rounded-2xl`}
                  >
                    <p className="py-[18px] text-lg font-extrabold leading-[22px]">
                      {show?.title}
                    </p>
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
